import type {AuthMode, AuthResult} from '../../state/authActions.js';

export const AUTH_CONTENT: Record<AuthMode, {description: string; submit: string; title: string}> = {
  'forgot-password': {
    description: 'Enter your account email to request a password reset link.',
    submit: 'Send reset link',
    title: 'Forgot your password?'
  },
  'resend-verification': {
    description: 'Request a new verification link for your account email.',
    submit: 'Resend verification',
    title: 'A fresh link.'
  },
  'reset-password': {
    description: 'Choose a new password. Your previous sessions will be signed out.',
    submit: 'Reset password',
    title: 'A fresh start.'
  },
  'sign-in': {
    description: 'Sign in to your account.',
    submit: 'Sign in',
    title: 'Welcome back.'
  },
  'sign-out': {
    description: 'End your account session in this browser.',
    submit: 'Sign out',
    title: 'Until your next idea.'
  },
  'sign-up': {
    description: 'Start free. No card required. Verify your email and start shaping your first story.',
    submit: 'Create account',
    title: 'Make room for your ideas.'
  },
  'verify-email': {
    description: 'Use the verification link from your email, or paste its token below.',
    submit: 'Verify email',
    title: 'One last step.'
  }
};

export interface AuthFormValues {
  readonly confirmPassword: string;
  readonly email: string;
  readonly name: string;
  readonly password: string;
  readonly token: string;
}

export const validateAuthForm = (mode: AuthMode, values: AuthFormValues): string => {
  if(['sign-in', 'sign-up', 'forgot-password', 'resend-verification'].includes(mode)
    && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    return 'Enter a valid email address.';
  }
  if(mode === 'sign-up' && !values.name.trim()) {
    return 'Enter your name.';
  }
  if(mode === 'sign-in' && !values.password) {
    return 'Enter your password.';
  }
  if(mode === 'sign-up' || mode === 'reset-password') {
    if(values.password !== values.confirmPassword) {
      return 'Your passwords do not match.';
    }
  }
  if((mode === 'verify-email' || mode === 'reset-password') && !values.token.trim()) {
    return 'This link is missing its token. Request a new link or paste the token from your email.';
  }
  return '';
};

export const getDemoDeliveryHref = (result: AuthResult): string | undefined => {
  const delivery = result.demoDelivery;
  if(result.deliveryMode !== 'demo' || !delivery?.token) {
    return undefined;
  }
  const route = delivery.kind === 'verification' ? 'verify-email' : 'reset-password';
  return `/${route}?token=${encodeURIComponent(delivery.token)}`;
};
